import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * @typedef {object} DrawerProductDetails
 * @property {string} url
 * @property {string} title
 * @property {string} [image]
 * @property {string} [price]
 */

/**
 * Promotes recently viewed products into the mobile drawer 2x2 grid.
 * Remaining slots keep the Liquid fallback (one piece per collection).
 */
class MenuDrawerFeatured extends HTMLElement {
  connectedCallback() {
    const viewedIds = RecentlyViewed.getProducts().map((id) => String(id));
    if (viewedIds.length === 0) return;

    const list = this.querySelector('[data-featured-list]');
    const titleEl = this.querySelector('[data-featured-title]');
    if (!(list instanceof HTMLElement)) return;

    const fallbackItems = Array.from(list.querySelectorAll('[data-product-id]'));
    /** @type {Map<string, Element>} */
    const fallbackById = new Map();
    for (const item of fallbackItems) {
      if (!(item instanceof HTMLElement) || !item.dataset.productId) continue;
      fallbackById.set(item.dataset.productId, item);
    }

    const details = RecentlyViewed.getDetails();
    /** @type {Element[]} */
    const nextItems = [];
    const used = new Set();
    const recentUsed = new Set();

    for (const id of viewedIds) {
      if (nextItems.length >= 4) break;
      const existing = fallbackById.get(id);
      if (existing) {
        nextItems.push(existing);
        used.add(id);
        recentUsed.add(id);
        continue;
      }
      const info = details[id];
      if (!info?.url || !info.title) continue;
      nextItems.push(this.#createItem(id, info));
      used.add(id);
      recentUsed.add(id);
    }

    for (const item of fallbackItems) {
      if (nextItems.length >= 4) break;
      if (!(item instanceof HTMLElement) || !item.dataset.productId) continue;
      if (used.has(item.dataset.productId)) continue;
      nextItems.push(item);
      used.add(item.dataset.productId);
    }

    if (nextItems.length === 0) return;

    list.replaceChildren(...nextItems.slice(0, 4));

    if (titleEl && recentUsed.size > 0) {
      titleEl.textContent = this.dataset.recentTitle || titleEl.textContent;
    }
  }

  /**
   * @param {string} id
   * @param {DrawerProductDetails} info
   * @returns {HTMLLIElement}
   */
  #createItem(id, info) {
    const li = document.createElement('li');
    li.className = 'menu-drawer__featured-content-list-item menu-drawer__featured-content-list-item--product';
    li.dataset.productId = id;

    const card = document.createElement('a');
    card.className = 'resource-card menu-drawer__featured-fallback-card';
    card.dataset.resourceType = 'product';
    card.href = info.url;

    const media = document.createElement('div');
    media.className = 'resource-card__media';
    media.style.setProperty('--resource-card-aspect-ratio', '1');

    if (info.image) {
      const img = document.createElement('img');
      img.className = 'resource-card__image';
      img.src = info.image;
      img.alt = info.title;
      img.loading = 'lazy';
      media.append(img);
    }

    const content = document.createElement('div');
    content.className = 'resource-card__content';

    const title = document.createElement('p');
    title.className = 'resource-card__title paragraph';
    title.textContent = info.title;
    content.append(title);

    if (info.price) {
      const price = document.createElement('div');
      price.className = 'price';
      price.textContent = info.price;
      content.append(price);
    }

    card.append(media, content);
    li.append(card);
    return li;
  }
}

if (!customElements.get('menu-drawer-featured')) {
  customElements.define('menu-drawer-featured', MenuDrawerFeatured);
}
